import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchUserProfile = async (user, accessToken = null) => {
    console.log('fetchUserProfile called with user:', user?.id);
    if (!user) {
      setUserProfile(null);
      return;
    }

    setProfileLoading(true);
    try {
      let token = accessToken;
      
      // If no token provided, get it from session
      if (!token) {
        console.log('No token provided, getting from session...');
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.access_token) {
          console.error('No access token available');
          setUserProfile(null);
          setProfileLoading(false);
          return;
        }
        
        token = session.access_token;
      }

      console.log('Calling users-me with token:', token.substring(0, 20) + '...');
      
      // Try direct HTTP request first
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/users-me`;
      console.log('Function URL:', functionUrl);
      
      try {
        const response = await fetch(functionUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        console.log('Direct fetch response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Direct fetch error:', errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Direct fetch data:', data);
        setUserProfile(data);
        return;
      } catch (fetchError) {
        console.error('Direct fetch failed:', fetchError);
        console.log('Falling back to supabase.functions.invoke...');
      }
      
      // Fallback to supabase.functions.invoke
      const { data, error } = await supabase.functions.invoke('users-me', {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      console.log('Supabase functions response:', { data, error });

      if (error) {
        console.error('Failed to fetch user profile:', error);
        console.error('Error details:', error.message, error.code);
        console.error('Full error object:', JSON.stringify(error, null, 2));
        setUserProfile(null);
      } else {
        console.log('User profile fetched successfully:', data);
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Exception in fetchUserProfile:', error);
      console.error('Error stack:', error.stack);
      console.error('Error name:', error.name);
      setUserProfile(null);
    } finally {
      console.log('Setting profileLoading to false');
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const handleOAuthCallback = async () => {
      console.log('AuthContext: Getting initial session...');
      console.log('AuthContext: Current URL:', window.location.href);
      console.log('AuthContext: Hash:', window.location.hash);
      
      // Handle Instagram Business OAuth callback
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('code') && window.location.pathname === '/dashboard') {
        console.log('AuthContext: Processing Instagram Business OAuth callback...');
        
        const code = urlParams.get('code');
        
        if (code) {
          try {
            // Call Instagram auth function with the code
            const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/instagram-auth`;
            const response = await fetch(`${functionUrl}?code=${code}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              }
            });
            
            if (!response.ok) {
              throw new Error('Instagram Business authentication failed');
            }
            
            // Clear the code from URL
            window.history.replaceState(null, null, '/dashboard');
            
            console.log('AuthContext: Instagram Business authentication processed successfully');
          } catch (err) {
            console.error('AuthContext: Exception processing Instagram Business auth:', err);
            alert('Instagram Businessでのログインに失敗しました。もう一度お試しください。');
          }
        }
        
        // Wait a bit for the auth state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Handle LINE OAuth callback
      if (urlParams.get('line_auth') === 'success') {
        console.log('AuthContext: Processing LINE OAuth callback...');
        
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          try {
            // Set the session using LINE tokens
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('AuthContext: Error setting LINE session:', error);
              alert('LINEでのログインに失敗しました。もう一度お試しください。');
            } else {
              console.log('AuthContext: LINE session set successfully');
              // Clear the parameters from URL
              window.history.replaceState(null, null, '/dashboard');
            }
          } catch (err) {
            console.error('AuthContext: Exception setting LINE session:', err);
            alert('LINEでのログインに失敗しました。もう一度お試しください。');
          }
        }
        
        // Wait a bit for the auth state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Handle Instagram OAuth callback (fallback for old implementation)
      if (urlParams.get('instagram_auth') === 'success') {
        console.log('AuthContext: Processing Instagram OAuth callback...');
        
        const accessToken = urlParams.get('access_token');
        const refreshToken = urlParams.get('refresh_token');
        
        if (accessToken && refreshToken) {
          try {
            // Set the session using Instagram tokens
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('AuthContext: Error setting Instagram session:', error);
              alert('Instagramでのログインに失敗しました。もう一度お試しください。');
            } else {
              console.log('AuthContext: Instagram session set successfully');
              // Clear the parameters from URL
              window.history.replaceState(null, null, '/dashboard');
            }
          } catch (err) {
            console.error('AuthContext: Exception setting Instagram session:', err);
            alert('Instagramでのログインに失敗しました。もう一度お試しください。');
          }
        }
        
        // Wait a bit for the auth state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Handle Instagram OAuth errors（公式仕様準拠）
      if (urlParams.get('error') === 'instagram_access_denied') {
        const errorMessage = urlParams.get('message') || 'ユーザーによりInstagram認証が拒否されました';
        console.error('AuthContext: Instagram access denied:', errorMessage);
        window.history.replaceState(null, null, '/login');
        alert(`Instagram認証がキャンセルされました: ${decodeURIComponent(errorMessage)}`);
        return;
      }
      
      if (urlParams.get('error') === 'instagram_auth_failed') {
        const errorMessage = urlParams.get('message') || 'Instagramでのログインに失敗しました';
        console.error('AuthContext: Instagram auth error:', errorMessage);
        window.history.replaceState(null, null, '/login');
        alert(`Instagram認証エラー: ${decodeURIComponent(errorMessage)}`);
        return;
      }

      // Handle LINE OAuth errors
      if (urlParams.get('error') === 'line_access_denied') {
        const errorMessage = urlParams.get('message') || 'ユーザーによりLINE認証が拒否されました';
        console.error('AuthContext: LINE access denied:', errorMessage);
        window.history.replaceState(null, null, '/login');
        alert(`LINE認証がキャンセルされました: ${decodeURIComponent(errorMessage)}`);
        return;
      }
      
      if (urlParams.get('error') === 'line_auth_failed') {
        const errorMessage = urlParams.get('message') || 'LINEでのログインに失敗しました';
        console.error('AuthContext: LINE auth error:', errorMessage);
        window.history.replaceState(null, null, '/login');
        alert(`LINE認証エラー: ${decodeURIComponent(errorMessage)}`);
        return;
      }
      
      // Handle OAuth callback - check for both success and error
      if (window.location.hash && (window.location.hash.includes('access_token') || window.location.hash.includes('error'))) {
        console.log('AuthContext: Processing OAuth callback hash...');
        
        // Check for OAuth errors
        if (window.location.hash.includes('error')) {
          const errorMatch = window.location.hash.match(/error_description=([^&]+)/);
          const errorDescription = errorMatch ? decodeURIComponent(errorMatch[1].replace(/\+/g, ' ')) : 'Unknown OAuth error';
          console.error('AuthContext: OAuth error:', errorDescription);
          
          // Clear error from URL and redirect to login
          window.history.replaceState(null, null, '/login');
          alert(`X認証エラー: ${errorDescription}`);
          return;
        }
        
        try {
          // Extract and process the hash fragment
          const { data, error } = await supabase.auth.getSession();
          console.log('AuthContext: getSession after hash processing:', { data, error });
          
          if (error) {
            console.error('AuthContext: Error processing OAuth callback:', error);
          } else {
            console.log('AuthContext: OAuth callback processed successfully');
            // Clear the hash from URL
            window.history.replaceState(null, null, window.location.pathname);
          }
        } catch (err) {
          console.error('AuthContext: Exception processing OAuth callback:', err);
        }
        
        // Wait a bit for the auth state to update
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      console.log('AuthContext: Final session user:', currentUser?.id);
      console.log('AuthContext: Final session token exists:', !!session?.access_token);
      setUser(currentUser);
      setLoading(false);
      
      if (currentUser && session?.access_token) {
        console.log('AuthContext: Fetching profile for initial user...');
        await fetchUserProfile(currentUser, session.access_token);
      }
    };

    handleOAuthCallback();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('AuthContext: Auth state changed:', event);
        const currentUser = session?.user ?? null;
        setUser(currentUser);
        setLoading(false);
        
        if (currentUser && session?.access_token && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          console.log('AuthContext: Fetching profile for auth change...');
          await fetchUserProfile(currentUser, session.access_token);
          
          // Redirect to dashboard after successful OAuth login (Google, Facebook, Twitter, etc.)
          if (event === 'SIGNED_IN' && (window.location.pathname === '/login' || window.location.pathname === '/')) {
            console.log('AuthContext: Redirecting to dashboard after OAuth login');
            window.location.href = '/dashboard';
          }
        } else if (event === 'SIGNED_OUT') {
          setUserProfile(null);
        }
      }
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const signUp = async (email, password, phone = null) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          data: phone ? { phone } : undefined
        }
      });
      
      if (error) {
        console.error('Signup error details:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error status:', error.status);
      }
      
      return { data, error };
    } catch (err) {
      console.error('Unexpected error during signup:', err);
      return { data: null, error: err };
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const signInWithProvider = async (provider) => {
    // プロバイダー別の最適化
    if (provider === 'twitter') {
      // X(Twitter) は OAuth2 で追加クエリは不要。
      // 最小権限のみ要求して同意画面の文言をシンプルにする。
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'twitter',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          scopes: 'tweet.read users.read offline.access'
        }
      });
      return { data, error };
    }

    if (provider === 'google') {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });
      return { data, error };
    }

    // デフォルト（Facebook など）
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    });
    return { data, error };
  };

  const signInWithInstagram = async () => {
    try {
      // Supabase Edge Functionを呼び出す際にanon keyを付与
      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/instagram-auth`;
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      // 認証ヘッダー付きでリダイレクト
      // ブラウザが直接リダイレクトできるようにURLにapikeyを含める
      window.location.href = `${functionUrl}?action=login&apikey=${anonKey}`;
    } catch (error) {
      console.error('Instagram authentication error:', error);
      return { data: null, error };
    }
  };

  const signInWithLine = async () => {
    try {
      // stateを生成してセッションストレージに保存
      const state = crypto.randomUUID();
      sessionStorage.setItem('line_oauth_state', state);
      
      // Supabase Functions invokeを使って認証URLを取得
      const { data, error } = await supabase.functions.invoke('line-auth', {
        body: { action: 'getAuthUrl', state: state }
      });
      
      if (error) {
        console.error('Error getting LINE auth URL:', error);
        throw error;
      }
      
      if (data?.authUrl) {
        // LINEの認証ページにリダイレクト
        window.location.href = data.authUrl;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (error) {
      console.error('LINE authentication error:', error);
      return { data: null, error };
    }
  };

  const updateUserProfile = async (profileData) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('users-me', {
        body: profileData,
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        }
      });

      if (error) {
        console.error('Failed to update user profile:', error);
        return { data: null, error };
      } else {
        setUserProfile(data);
        return { data, error: null };
      }
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { data: null, error };
    } finally {
      setProfileLoading(false);
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    signInWithProvider,
    signInWithInstagram,
    signInWithLine,
    updateUserProfile,
    fetchUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
